import React, { createContext, useState, useEffect, useContext, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import billingService, { BillingInfo } from '../services/billingService';

interface BillingContextType {
  billingInfo: BillingInfo | null;
  isLoading: boolean;
  refreshBillingInfo: () => Promise<void>;
}

const BillingContext = createContext<BillingContextType>({
  billingInfo: null,
  isLoading: false,
  refreshBillingInfo: async () => {},
});

export const useBilling = () => useContext(BillingContext);

interface BillingProviderProps {
  children: ReactNode;
}

/**
 * BillingProvider fetches billing info once and shares it across all authenticated
 * components (DashboardLayout sidebar, ProfileDropdown, BillingSettings, etc.)
 *
 * Previously, DashboardLayout and ProfileDropdown each independently fetched
 * /api/billing/current on every page mount, resulting in 2 API calls per
 * navigation. This provider reduces that to 1 call per session, with a
 * refreshBillingInfo() method for explicit refresh after billing actions.
 */
export const BillingProvider: React.FC<BillingProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshBillingInfo = useCallback(async () => {
    if (!isAuthenticated || !user?.organizationId) return;
    try {
      setIsLoading(true);
      const info = await billingService.getCurrentBillingInfo();
      setBillingInfo(info);
    } catch (error) {
      // Silently fail — billing info is optional for display (upgrade button, plan name).
      // Components that absolutely require billing data (BillingSettings) can fetch directly.
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user?.organizationId]);

  // Fetch billing info when user is authenticated and has an organization
  useEffect(() => {
    if (isAuthenticated && user?.organizationId) {
      refreshBillingInfo();
    }
  }, [isAuthenticated, user?.organizationId, refreshBillingInfo]);

  return (
    <BillingContext.Provider value={{ billingInfo, isLoading, refreshBillingInfo }}>
      {children}
    </BillingContext.Provider>
  );
};

export default BillingContext;
